/**
 * Food Pulse — top-level Information Architecture manifest.
 *
 * A single source of truth for the nav bar, the section landing
 * pages, and every "sub-tile" grid. Adding a new section here
 * automatically:
 *   • Shows the section in the thin nav (with a dropdown if it has
 *     children).
 *   • Provides the `/food-pulse/<slug>` route with a section landing
 *     page.
 *   • Auto-renders each child as a tile card on that landing page.
 *
 * Each section can carry:
 *   - `to`         — direct route (used when the section is a single
 *                    page, no landing needed)
 *   - `route`      — the landing page path
 *   - `icon`       — lucide-react icon name (string, resolved in UI)
 *   - `tagline`    — one-liner shown under the hero H1
 *   - `blurb`      — longer supporting paragraph
 *   - `children`   — array of subsections. Each child has {label, to,
 *                    tagline?, icon?} — `to` may be a hash link,
 *                    absolute route, or query-string filter of an
 *                    existing directory.
 *
 * We deliberately store this as a plain manifest (not JSX) so the
 * same object can drive: (1) desktop nav, (2) mobile drawer,
 * (3) sitemap.xml, (4) admin-editable content later.
 */

import { FP_TAGLINES } from "./foodPulseTaglines";

export const FP_SIDE_TABS = [
  { to: "/",       label: "Explore Social Pulse", accent: "#7C3AED" },
  { to: "/nature", label: "Nature Pulse",         accent: "#059669" },
];

/**
 * Prominent orange quick-links surfaced above the main FoodPulse nav.
 * Each entry links to a real page — either an articles listing filtered
 * by tag/category, or a curated landing page. Meant to grab attention
 * and drive readers into evergreen editorial content.
 */
export const FP_QUICK_LINKS = [
  {
    label: "What is in Your Food?",
    to: "/food-pulse/articles/what-is-in-your-food",
    icon: "flask",
    accent: "#EA580C",
    emphasis: true,   // bolder styling — hero caption
  },
  {
    label: "Donate Food",
    to: "/food-pulse/food-waste-guide/donate-food",
    icon: "heart",
    accent: "#EA580C",
  },
  {
    label: "Food Charities",
    to: "/food-pulse/community/charities",
    icon: "gift",
    accent: "#EA580C",
  },
  {
    label: "Volunteer",
    to: "/food-pulse/community/volunteers",
    icon: "users",
    accent: "#EA580C",
  },
  {
    label: "Label Scanner",
    to: "/food-pulse/label-scan",
    icon: "camera",
    accent: "#EA580C",
    badge: "New",
  },
  {
    label: "Product Database",
    to: "/food-pulse/products",
    icon: "package",
    accent: "#8B5E10",
    badge: "New",
  },
  {
    label: "Urban Harvest",
    to: "/food-pulse/urban-harvest",
    icon: "trees",
    accent: "#3E7C4B",
    badge: "New",
  },
];

export const FP_SECTIONS = [
  // Home — single link, no dropdown.
  {
    slug: "home",
    label: "Home",
    to: "/food-pulse",
    end: true,
    icon: "home",
    hidden_from_landing: true,
  },

  // ─── About ─────────────────────────────────────────────────
  {
    slug: "about",
    label: "About",
    route: "/food-pulse/about",
    icon: "info",
    tagline: FP_TAGLINES.about,
    blurb: "Food Pulse is a global network connecting people, food, and communities. Learn who we are and what we stand for.",
    children: [
      { label: "Our Mission",   to: "/food-pulse/about#mission" },
      { label: "Team",          to: "/food-pulse/about#team" },
      { label: "Partners",      to: "/food-pulse/about#partners" },
      { label: "Volunteer",     to: "/food-pulse/about#volunteer" },
      { label: "Advertise",     to: "/food-pulse/about#advertise" },
      { label: "Privacy Policy",to: "/policies/foodpulse-privacy-policy" },
      { label: "Cookie Policy", to: "/policies/foodpulse-cookie-policy"  },
      { label: "Terms of Service", to: "/food-pulse/legal/terms" },
    ],
  },

  // ─── Discover (Places directory) ─────────────────────────
  {
    slug: "discover",
    label: "Discover",
    route: "/food-pulse/businesses",
    icon: "compass",
    tagline: FP_TAGLINES.food_discovery,
    blurb: "Find great food near you — restaurants, cafés, bakeries, farmers markets, grocery stores, food trucks, and local producers.",
    children: [
      { label: "All of Discover",   to: "/food-pulse/discover/all" },
      { label: "Restaurants",       to: "/food-pulse/discover/restaurants",       tagline: FP_TAGLINES.restaurants },
      { label: "Cafés & Coffee",    to: "/food-pulse/discover/cafes-coffee" },
      { label: "Bakeries",          to: "/food-pulse/discover/bakeries" },
      { label: "Farmers Markets",   to: "/food-pulse/discover/farmers-markets",   tagline: FP_TAGLINES.farmers_markets },
      { label: "Grocery Stores",    to: "/food-pulse/discover/grocery-stores",    tagline: FP_TAGLINES.groceries },
      { label: "Food Trucks",       to: "/food-pulse/discover/food-trucks" },
      { label: "Local Producers",   to: "/food-pulse/discover/local-producers" },
      { label: "Specialty Stores",  to: "/food-pulse/discover/specialty-stores" },
      { label: "Food Festivals",    to: "/food-pulse/discover/food-festivals" },
    ],
  },

  // ─── Food (encyclopedia + filters) ───────────────────────
  {
    slug: "food",
    label: "Food",
    route: "/food-pulse/food",
    icon: "utensils",
    tagline: "Every food, one living reference.",
    blurb: "The Food Pulse encyclopedia — nutrition, seasonality, recipes and cultural notes for every ingredient in your kitchen.",
    children: [
      { label: "Fruits",           to: "/food-pulse/food?category=fruits" },
      { label: "Vegetables",       to: "/food-pulse/food?category=vegetables,leafy-greens" },
      { label: "Meat & Poultry",   to: "/food-pulse/food?category=meat,poultry" },
      { label: "Fish & Seafood",   to: "/food-pulse/food?category=fish,seafood,shellfish" },
      { label: "Dairy & Eggs",     to: "/food-pulse/food?category=dairy,eggs" },
      { label: "Grains & Cereals", to: "/food-pulse/food?category=grains" },
      { label: "Beans & Lentils",  to: "/food-pulse/food?category=legumes" },
      { label: "Nuts & Seeds",     to: "/food-pulse/food?category=nuts" },
      { label: "Spices & Herbs",   to: "/food-pulse/food?category=spices,herbs" },
      { label: "Fermented Foods",  to: "/food-pulse/food?category=fermented" },
      { label: "Packaged Foods",   to: "/food-pulse/food?category=packaged" },
      { label: "Beverages",        to: "/food-pulse/food?category=beverages" },
    ],
  },

  // ─── Recipes ──────────────────────────────────────────────
  {
    slug: "recipes",
    label: "Recipes",
    route: "/food-pulse/recipes",
    icon: "book-open",
    tagline: FP_TAGLINES.recipes,
    blurb: "Recipes built around the food you already have — with a bias toward less waste, more nourishment, and more shared tables.",
    children: [
      { label: "Breakfast",              to: "/food-pulse/recipes/breakfast" },
      { label: "Lunch",                  to: "/food-pulse/recipes/lunch" },
      { label: "Dinner",                 to: "/food-pulse/recipes/dinner" },
      { label: "Desserts",               to: "/food-pulse/recipes/desserts" },
      { label: "Soups",                  to: "/food-pulse/recipes/soups" },
      { label: "Salads",                 to: "/food-pulse/recipes/salads" },
      { label: "Vegetarian",             to: "/food-pulse/recipes/vegetarian" },
      { label: "Vegan",                  to: "/food-pulse/recipes/vegan" },
      { label: "Gluten Free",            to: "/food-pulse/recipes/gluten-free" },
      { label: "Quick Meals",            to: "/food-pulse/recipes/quick" },
      { label: "Holiday Recipes",        to: "/food-pulse/recipes/holiday" },
      { label: "Regional Cuisine",       to: "/food-pulse/recipes/regional" },
      { label: "International Cuisine",  to: "/food-pulse/recipes/international" },
      // Cuisine-specific tabs — each seeds ~25 authentic recipes.
      { label: "Indian Cuisine",         to: "/food-pulse/recipes/indian" },
      { label: "Chinese Cuisine",        to: "/food-pulse/recipes/chinese" },
      { label: "Italian Cuisine",        to: "/food-pulse/recipes/italian" },
      { label: "Thai Cuisine",           to: "/food-pulse/recipes/thai" },
      { label: "Japanese Cuisine",       to: "/food-pulse/recipes/japanese" },
      { label: "Korean Cuisine",         to: "/food-pulse/recipes/korean" },
      { label: "French Cuisine",         to: "/food-pulse/recipes/french" },
      { label: "Greek Cuisine",          to: "/food-pulse/recipes/greek" },
      { label: "Lebanese Cuisine",       to: "/food-pulse/recipes/lebanese" },
      { label: "Kerala Cuisine",         to: "/food-pulse/recipes/kerala" },
      { label: "Sri Lankan Cuisine",     to: "/food-pulse/recipes/sri-lankan" },
      { label: "Punjabi Cuisine",        to: "/food-pulse/recipes/punjabi" },
      { label: "Goan Cuisine",           to: "/food-pulse/recipes/goan" },
      { label: "Gujarati Cuisine",       to: "/food-pulse/recipes/gujarati" },
    ],
  },

  // ─── Health ───────────────────────────────────────────────
  {
    slug: "health",
    label: "Health",
    route: "/food-pulse/health",
    icon: "heart",
    tagline: FP_TAGLINES.healthy_eating,
    blurb: "Practical nutrition and healthy-eating guidance for every age, every kitchen, every budget.",
    children: [
      { label: "Nutrition",           to: "/food-pulse/health/nutrition",         tagline: FP_TAGLINES.nutrition },
      { label: "Healthy Eating",      to: "/food-pulse/health/healthy-eating" },
      { label: "Weight Management",   to: "/food-pulse/health/weight-management" },
      { label: "Diabetes",            to: "/food-pulse/health/diabetes" },
      { label: "Heart Health",        to: "/food-pulse/health/heart-health" },
      { label: "Children's Nutrition",to: "/food-pulse/health/childrens-nutrition" },
      { label: "Senior Nutrition",    to: "/food-pulse/health/senior-nutrition" },
      { label: "Food Allergies",      to: "/food-pulse/health/allergies" },
      { label: "Food Safety",         to: "/food-pulse/health/safety" },
      { label: "Supplements",         to: "/food-pulse/health/supplements" },
      { label: "Cholesterol",         to: "/food-pulse/health/cholesterol" },
      { label: "Blood Pressure",      to: "/food-pulse/health/blood-pressure" },
      { label: "Gut Health",          to: "/food-pulse/health/gut-health" },
      { label: "Sleep Nutrition",     to: "/food-pulse/health/sleep-nutrition" },
    ],
  },

  // ─── Deals ────────────────────────────────────────────────
  {
    slug: "deals",
    label: "Deals",
    route: "/food-pulse/deals",
    icon: "tag",
    tagline: FP_TAGLINES.food_deals,
    blurb: "Save money while wasting less food. Local grocery deals, restaurant specials, and community coupons updated daily.",
    children: [
      { label: "All of Deals",        to: "/food-pulse/deals-guide/all" },
      { label: "Grocery Deals",       to: "/food-pulse/deals-guide/grocery" },
      { label: "Restaurant Specials", to: "/food-pulse/deals-guide/restaurant" },
      { label: "Coupons",             to: "/food-pulse/deals-guide/coupons",   tagline: FP_TAGLINES.coupons },
      { label: "Clearance Items",     to: "/food-pulse/deals-guide/clearance" },
      { label: "Flash Sales",         to: "/food-pulse/deals-guide/flash-sales" },
      { label: "Cashback",            to: "/food-pulse/deals-guide/cashback" },
      { label: "Loyalty Programs",    to: "/food-pulse/deals-guide/loyalty" },
    ],
  },

  // ─── Community ────────────────────────────────────────────
  {
    slug: "community",
    label: "Community",
    route: "/food-pulse/community",
    icon: "users",
    tagline: FP_TAGLINES.community,
    blurb: "Where food brings people together. Discussion boards, food reviews, volunteering, and local groups.",
    children: [
      { label: "All of Community",     to: "/food-pulse/community/all" },
      { label: "Discussion Forums",    to: "/food-pulse/community/forums",       tagline: FP_TAGLINES.discussion_boards },
      { label: "Questions & Answers",  to: "/food-pulse/community/q-and-a" },
      { label: "Food Reviews",         to: "/food-pulse/community/reviews",      tagline: FP_TAGLINES.reviews },
      { label: "Volunteers",           to: "/food-pulse/community/volunteers",   tagline: FP_TAGLINES.volunteer },
      { label: "Food Assistance",      to: "/food-pulse/community/assistance",   tagline: FP_TAGLINES.food_assistance },
      { label: "Food Charities",       to: "/food-pulse/community/charities",    tagline: FP_TAGLINES.food_charities },
      { label: "Local Groups",         to: "/food-pulse/community/local-groups" },
      { label: "Events",               to: "/food-pulse/community/events",       tagline: FP_TAGLINES.events },
    ],
  },

  // ─── Farming ──────────────────────────────────────────────
  {
    slug: "farming",
    label: "Farming",
    route: "/food-pulse/farming",
    icon: "sprout",
    tagline: FP_TAGLINES.farmers,
    blurb: "From the farm to your family — profiles of the growers, ranchers, and food producers behind every plate.",
    children: [
      { label: "All of Farming",         to: "/food-pulse/farming/all" },
      { label: "Farmers",                to: "/food-pulse/farming/farmers",             tagline: FP_TAGLINES.farmers },
      { label: "Farms & Ranches",        to: "/food-pulse/farming/farms-ranches" },
      { label: "Farmers Markets",        to: "/food-pulse/farming/farmers-markets",     tagline: FP_TAGLINES.farmers_markets },
      { label: "Home Garden Registry",   to: "/food-pulse/farming/home-garden-registry" },
      { label: "Regenerative Ag.",       to: "/food-pulse/farming/regenerative" },
      { label: "Climate & Agriculture",  to: "/food-pulse/farming/climate" },
    ],
  },

  // ─── Food Waste ───────────────────────────────────────────
  {
    slug: "food-waste",
    label: "Food Waste",
    route: "/food-pulse/articles?category=food-waste",
    icon: "recycle",
    tagline: FP_TAGLINES.food_waste,
    blurb: "Rescue food. Protect our planet. Practical actions to fight food waste in your kitchen, community, and country.",
    children: [
      { label: "All of Food Waste",   to: "/food-pulse/food-waste-guide/all" },
      { label: "Food Rescue",         to: "/food-pulse/food-waste-guide/food-rescue" },
      { label: "Surplus Food",        to: "/food-pulse/food-waste-guide/surplus-food" },
      { label: "Donate Food",         to: "/food-pulse/food-waste-guide/donate-food",           tagline: FP_TAGLINES.food_donations },
      { label: "Composting",          to: "/food-pulse/food-waste-guide/composting" },
      { label: "Zero Waste Tips",     to: "/food-pulse/food-waste-guide/zero-waste-tips" },
      { label: "Leftover Recipes",    to: "/food-pulse/food-waste-guide/leftover-recipes" },
      { label: "Sustainability",      to: "/food-pulse/food-waste-guide/sustainability",        tagline: FP_TAGLINES.sustainability },
      { label: "Food Recovery Orgs.", to: "/food-pulse/food-waste-guide/recovery-organizations" },
      { label: "Food Charities",      to: "/food-pulse/community/charities",                    tagline: FP_TAGLINES.food_charities },
      { label: "Volunteers",          to: "/food-pulse/community/volunteers",                   tagline: FP_TAGLINES.volunteer },
      { label: "Urban Harvest",       to: "/food-pulse/urban-harvest",                          tagline: "Register your backyard fruit tree" },
    ],
  },

  // ─── Places ───────────────────────────────────────────────
  {
    slug: "places",
    label: "Places",
    route: "/food-pulse/places",
    icon: "map-pin",
    tagline: "Every food place has a story.",
    blurb: "Browse every food business on Food Pulse by country, state, or city — plus the restaurants, farms and markets that define each place.",
    children: [
      { label: "All of Places",     to: "/food-pulse/places/all" },
      { label: "Countries",         to: "/food-pulse/places/countries" },
      { label: "States",            to: "/food-pulse/places/states" },
      { label: "Cities",            to: "/food-pulse/places/cities" },
      { label: "Neighborhoods",     to: "/food-pulse/places/neighborhoods" },
      { label: "Restaurants",       to: "/food-pulse/places/restaurants" },
      { label: "Grocery Stores",    to: "/food-pulse/places/grocery-stores" },
      { label: "Farms",             to: "/food-pulse/places/farms" },
      { label: "Vineyards",         to: "/food-pulse/places/vineyards" },
      { label: "Markets",           to: "/food-pulse/places/markets" },
      { label: "Parks & Attractions", to: "/food-pulse/places/parks-attractions" },
    ],
  },

  // ─── Articles ─────────────────────────────────────────────
  {
    slug: "articles",
    label: "Articles",
    to: "/food-pulse/articles",
    icon: "newspaper",
    hidden_from_landing: true,
  },

  // ─── News ─────────────────────────────────────────────────
  {
    slug: "news",
    label: "News",
    route: "/food-pulse/news/all",
    icon: "megaphone",
    tagline: FP_TAGLINES.food_news,
    blurb: "The latest from the food industry, agriculture, government, technology, and climate.",
    children: [
      { label: "All of News",           to: "/food-pulse/news/all" },
      { label: "Food Industry",         to: "/food-pulse/news/food-industry" },
      { label: "Agriculture",           to: "/food-pulse/news/agriculture" },
      { label: "Nutrition Research",    to: "/food-pulse/news/nutrition-research" },
      { label: "Restaurant News",       to: "/food-pulse/news/restaurant" },
      { label: "Government Regulations",to: "/food-pulse/news/regulations" },
      { label: "Product Recalls",       to: "/food-pulse/news/recalls" },
      { label: "Sustainability",        to: "/food-pulse/news/sustainability" },
      { label: "Technology",            to: "/food-pulse/news/technology" },
    ],
  },

  // ─── Marketplace ──────────────────────────────────────────
  {
    slug: "marketplace",
    label: "Marketplace",
    route: "/food-pulse/marketplace",
    icon: "shopping-bag",
    tagline: FP_TAGLINES.marketplace,
    blurb: "Buy, sell, share, sustain. Local food, home-baked goods, kitchen equipment, and community services.",
    children: [
      { label: "All of Marketplace", to: "/food-pulse/marketplace/all" },
      { label: "Buy & Sell",         to: "/food-pulse/marketplace/buy-sell" },
      { label: "Farmers",            to: "/food-pulse/marketplace/farmers" },
      { label: "Home Bakers",        to: "/food-pulse/marketplace/home-bakers" },
      { label: "Catering",           to: "/food-pulse/marketplace/catering" },
      { label: "Kitchen Equipment",  to: "/food-pulse/marketplace/kitchen-equipment" },
      { label: "Classifieds",        to: "/food-pulse/marketplace/classifieds" },
      { label: "Jobs",               to: "/food-pulse/marketplace/jobs" },
      { label: "Services",           to: "/food-pulse/marketplace/services" },
    ],
  },

  // ─── Contact ──────────────────────────────────────────────
  {
    slug: "contact",
    label: "Contact",
    to: "/contact",
    icon: "message-circle",
    hidden_from_landing: true,
  },
];

// Lookup helpers -----------------------------------------------------
export function findFpSection(slug) {
  return FP_SECTIONS.find((s) => s.slug === slug) || null;
}
